import { useCallback, useMemo } from 'react'
import { OverviewItemProps } from '../../OverviewCard/OverviewCard.types'
import TargetIcon from '@/assets/images/target-icon.svg?react'
import AssetsIcon from '@/assets/images/assets-icon.svg?react'
import CloudSecurityIcon from '@/assets/images/cloud-security-icon.svg?react'
import CloudIcon from '@/assets/images/cloud-icon.svg?react'
import EmailProtectionIcon from '@/assets/images/fd_overview_icon.svg?react'
import EmailIcon from '@/assets/images/email-icon.svg?react'
import MDRShieldIcon from '@/assets/images/mdr_overview_icon.svg?react'
import UserLaptopIcon from '@/assets/images/user-laptop-icon.svg?react'
import { AccountModule } from '@/core/modules/account/account.module'
import { AssetsModule } from '@/core/modules/digital-footprint/assets/assets.module'
import { IssuesModule } from '@/core/modules/issues'
import { UsersModule } from '@/core/modules/users'
import { useRouting } from '@/core/hooks/useRouting'
import { IntegrationsStore } from '@/core/store/Integrations'
import { usePendingProviders } from '@/core/hooks/usePendingProviders/usePendingProviders'

const useOverview = () => {
    const { navigateTo, ROUTES } = useRouting()
    const { isMdrActive } = AccountModule.useStore()
    const { data: assets } = AssetsModule.useStore()
    const numberOfCloudApps = AssetsModule.getCloudApps().length
    const {
        isMicrosoftFraudDefenseLoading,
        isGoogleFraudDefenseLoading,
        isGoogleLoading,
        isMicrosoftLoading,
    } = usePendingProviders()

    const isFDLoading =
        isGoogleFraudDefenseLoading || isMicrosoftFraudDefenseLoading
    const isCloudAppsLoading = isGoogleLoading || isMicrosoftLoading

    const numberOfGoogleBrokenIntegrations =
        IntegrationsStore.getBrokenIntegrationsByType('google').length
    const numberOfMicrosoftBrokenIntegrations =
        IntegrationsStore.getBrokenIntegrationsByType('microsoft').length

    const numberOfGoogleActiveIntegrations =
        IntegrationsStore.getActiveIntegrationsByType('google').length

    const numberOfMicrosoftActiveIntegrations =
        IntegrationsStore.getActiveIntegrationsByType('microsoft').length

    const googleFDActiveIntegrations =
        IntegrationsStore.getActiveIntegrationsByType('google_fraud_defense')

    const microsoftFDActiveIntegrations =
        IntegrationsStore.getActiveIntegrationsByType('ms_fraud_defense')

    const hasDisabledFDSecurityServices =
        IntegrationsStore.getNumberOfDisabledFDSecurityServices() > 0

    const numberOfGoogleFDActiveIntegrations = googleFDActiveIntegrations.length
    const numberOfMicrosoftFDActiveIntegrations =
        microsoftFDActiveIntegrations.length

    const numberOfActiveCloudApps =
        numberOfGoogleActiveIntegrations + numberOfMicrosoftActiveIntegrations

    const numberOfActiveFDIntegrations =
        numberOfGoogleFDActiveIntegrations +
        numberOfMicrosoftFDActiveIntegrations

    const isCloudAppsActive = numberOfActiveCloudApps > 0
    const isFDActive = numberOfActiveFDIntegrations > 0
    const areAllFDSecurityServicesDisabled =
        IntegrationsStore.areAllFraudDefenseSecurityServicesDisabled()

    const getNumberOfAssets = useCallback(() => {
        return assets.filter(
            (asset) =>
                asset.asset_type === 'email' ||
                asset.asset_type === 'domain' ||
                asset.asset_type === 'subdomain'
        ).length
    }, [assets])

    const cloudAppsTitle =
        numberOfCloudApps === 0
            ? 'No Cloud Apps'
            : numberOfCloudApps === 1
              ? 'Cloud App'
              : 'Cloud Apps'

    const isFDBroken = IntegrationsStore.isFDBroken()

    const isCloudAppHasBrokenIntegrations =
        numberOfGoogleBrokenIntegrations > 0 ||
        numberOfMicrosoftBrokenIntegrations > 0

    const allIssues = IssuesModule.getIssues({ disableFilters: true })
    const allFdIssues = UsersModule.getFdIssues({
        disableFilters: true,
    })

    const issuesCountByType: { cloudIssues: number; generalIssues: number } =
        useMemo(() => {
            return allIssues
                .filter((issue) => issue.status === 'open')
                .reduce(
                    (acc, issue) => {
                        const issueSize =
                            (issue.assets?.length ?? 0) +
                            (issue.technologies?.length ?? 0) +
                            (issue.devices?.length ?? 0)
                        if (issue.issue_type === ('CLOUD_APP' as TIssueType)) {
                            acc.cloudIssues += issueSize
                        } else {
                            acc.generalIssues += issueSize
                        }
                        return acc
                    },
                    { cloudIssues: 0, generalIssues: 0 }
                )
        }, [allIssues])

    const fraudDefenseAlertCount = useMemo(
        () => allFdIssues.filter((issue) => issue.status === 'open').length,
        [allFdIssues]
    )

    const isCloudAppSecurityActive =
        isCloudAppsActive || isCloudAppHasBrokenIntegrations || isFDActive

    const handleActivateFraudDefenseClick = () => {
        navigateTo({
            path: ROUTES.fraud_defense.children.configurations.path,
            event: {
                name: 'SEM::navigate_to_fraud_defense_configurations_page',
                params: {
                    origin: 'my_stance_overview_fraud_defense_card',
                    cardId: 'fraud-defense',
                    isActive: isFDActive,
                    alertCount: fraudDefenseAlertCount,
                },
            },
        })
    }

    const handleNavigateToMdrPage = () => {
        navigateTo({
            path: ROUTES.mdr.path,
            event: {
                name: 'SEM::navigate_to_mdr_page',
                params: {
                    origin: 'my_stance_overview_mdr_card',
                    cardId: 'mdr',
                    isActive: isMdrActive,
                    // Also under the sidebar's key, so the event can be broken
                    // down by active state across every origin at once.
                    isMdrActive,
                },
            },
        })
    }

    const handleNavigateToIntegrationsPage = () => {
        navigateTo({
            path: ROUTES.integrations.path,
            event: {
                name: 'SEM::navigate_to_integrations_page',
                params: {
                    origin: 'my_stance_overview_integrations_card',
                    cardId: 'cloud-app-security',
                    isActive: isCloudAppSecurityActive,
                    alertCount: issuesCountByType.cloudIssues,
                },
            },
        })
    }

    const overviewItems: OverviewItemProps[] = [
        {
            id: 'exposure-scanner',
            featureTitle: 'Exposure Scanner',
            isActive: true,
            featureIcon: <TargetIcon />,
            monitoringStatsTitle:
                getNumberOfAssets() === 0
                    ? 'No Network Assets'
                    : 'Network Assets',
            monitoringStatsSubtitle: 'monitored for exposures',
            monitoringStatsIcon: <AssetsIcon width={18} height={18} />,
            monitoringStatsCount:
                getNumberOfAssets() === 0 ? undefined : getNumberOfAssets(),
            alertCount: issuesCountByType.generalIssues,
            alertTooltipText: 'Open issues on these assets',
        },
        {
            id: 'cloud-app-security',
            featureTitle: 'Cloud App Security',
            isActive: isCloudAppSecurityActive,
            hasConnectionError: isCloudAppHasBrokenIntegrations || isFDBroken,
            featureIcon: <CloudSecurityIcon />,
            monitoringStatsTitle: cloudAppsTitle,
            monitoringStatsSubtitle: 'monitored for issues',
            monitoringStatsIcon: <CloudIcon width={18} height={18} />,
            monitoringStatsCount:
                numberOfCloudApps === 0 ? undefined : numberOfCloudApps,
            alertCount: issuesCountByType.cloudIssues,
            alertTooltipText: 'Open issues on these assets',
            onClick: handleNavigateToIntegrationsPage,
            isLoading: isCloudAppsLoading || isFDLoading,
        },
        {
            id: 'fraud-defense',
            featureTitle: 'Fraud Defense',
            isActive: isFDActive,
            hasConnectionError: hasDisabledFDSecurityServices || isFDBroken,
            featureIcon: <EmailProtectionIcon />,
            monitoringStatsTitle:
                areAllFDSecurityServicesDisabled || !isFDActive
                    ? 'No User Mailboxes'
                    : 'User Mailboxes',
            monitoringStatsSubtitle: 'monitored for fraud',
            monitoringStatsIcon: <EmailIcon width={16} height={16} />,
            monitoringStatsCount: undefined,
            alertCount: fraudDefenseAlertCount,
            alertTooltipText: 'Open issues on these assets',
            onClick: handleActivateFraudDefenseClick,
            isLoading: isFDLoading,
        },
        {
            id: 'mdr',
            featureTitle: 'MDR',
            isActive: isMdrActive,
            featureIcon: <MDRShieldIcon width={20} height={22} />,
            monitoringStatsTitle: 'Monitoring',
            monitoringStatsSubtitle: isMdrActive
                ? 'Protecting your business'
                : 'not available',
            monitoringStatsIcon: <UserLaptopIcon width={20} height={20} />,
            monitoringStatsCount: '24x7',
            onClick: handleNavigateToMdrPage,
        },
    ]

    return {
        overviewItems,
    }
}

export default useOverview
